import { useEffect, useState, type KeyboardEvent, type ReactNode } from 'react';
import { isGroupSeparatorKey, readScan, scanFillValues, scanReadout, suffixKey } from './scan';
import type { WidgetProps } from './types';

/**
 * Champ de saisie d'un code à la douchette (clavier HID) : la rafale s'accumule et n'est lue qu'au suffixe
 * déclaré (`presentation.scan.suffix`, Entrée par défaut) ; le séparateur GS1 tapé (U+001D ou Ctrl+]) est
 * gardé. Partagé par `scan_code` et `lot`. `onRead` reçoit le code brut lu, aussi quand le champ perd le
 * focus sur une saisie non lue ; `onType` (facultatif) reçoit chaque frappe (saisie à la main du `lot`).
 */
export function ScanInput({ id, value, onRead, onType, disabled, invalid, describedBy, suffix, placeholder }: { id: string; value: string; onRead: (raw: string) => void; onType?: (raw: string) => void; disabled: boolean; invalid: boolean; describedBy: string | undefined; suffix: 'enter' | 'tab' | undefined; placeholder: string }): ReactNode {
  const [buffer, setBuffer] = useState(value);
  // Une valeur reçue remplace la saisie, sauf si elle n'en diffère que par les espaces de bord (saisie en cours).
  useEffect(() => setBuffer((current) => (current.trim() === value.trim() ? current : value)), [value]);
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === suffixKey(suffix)) {
      // Fin de rafale : le code est lu ; Entrée ne soumet jamais la fiche, Tab reste dans le champ.
      event.preventDefault();
      onRead(buffer);
    } else if (isGroupSeparatorKey(event.key, event.ctrlKey)) {
      event.preventDefault();
      setBuffer((current) => `${current}\u001d`);
    }
  };
  return (
    <input
      id={id}
      type="text"
      className="ay-scan__input"
      autoComplete="off"
      autoCapitalize="off"
      spellCheck={false}
      placeholder={placeholder}
      disabled={disabled}
      aria-invalid={invalid}
      aria-describedby={describedBy}
      value={buffer}
      onChange={(event) => {
        setBuffer(event.target.value);
        onType?.(event.target.value);
      }}
      onBlur={() => {
        if (buffer.trim() !== value.trim()) onRead(buffer);
      }}
      onKeyDown={onKeyDown}
    />
  );
}

/**
 * Widget `scan_code` (ADR-146 §6.1, addendum (6)) d'un champ texte : la réponse est le code brut lu ; sa
 * lecture GS1 (analyseur unique du Core) s'affiche en clair et remplit les champs déclarés
 * (`presentation.scan.fills`) du même groupe. Un code illisible se signale quand des champs sont à
 * remplir ; le serveur relit le code et refuse tout écart.
 */
export function ScanCode({ id, field, value, onChange, disabled, invalid, describedBy, today, scan, onFill }: WidgetProps): ReactNode {
  if (field.type !== 'string' && field.type !== 'text') return null;
  const raw = typeof value === 'string' ? value : '';
  const result = raw === '' ? null : readScan(raw, today);
  const fills = scan?.fills;
  const read = (next: string): void => {
    if (next.trim() === '') {
      onChange(null);
      return;
    }
    onChange(next);
    const filled = scanFillValues(readScan(next, today), fills);
    if (Object.keys(filled).length > 0) onFill?.(filled);
  };
  const lines = result === null ? [] : scanReadout(result);
  return (
    <div className="ay-scan">
      <span className="ay-scan__row">
        <ScanInput id={id} value={raw} onRead={read} disabled={disabled} invalid={invalid} describedBy={describedBy} suffix={scan?.suffix} placeholder="Scannez ou tapez le code, puis Entrée" />
        {raw !== '' && !disabled && (
          <button type="button" className="ay-button ay-button--secondary ay-scan__clear" onClick={() => onChange(null)}>
            Effacer
          </button>
        )}
      </span>
      {lines.length > 0 && (
        <dl className="ay-scan__readout" data-testid={`scan-readout-${id}`}>
          {lines.map((line) => (
            <div key={line.name} className="ay-scan__item">
              <dt>{line.label}</dt>
              <dd>{line.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {result !== null && !result.ok && fills !== undefined && (
        <span className="ay-field__error" role="alert">
          Code illisible : {result.error.message}
        </span>
      )}
    </div>
  );
}
